import { useState, useRef, useCallback, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link,
  Image,
  Type,
  Heading1,
  Heading2,
  Palette,
  Minus,
  Undo,
  Redo,
  Code,
  Upload,
  Loader2,
} from "lucide-react";
import api from "@/lib/api-client";
import {
  BRAND_COLORS,
  wrapEmailContent,
  type EmailLayoutVariant,
} from "@/lib/email-layout";

/**
 * Estilo del botón que inserta la barra de herramientas.
 *
 * Comparte color con los botones de las plantillas y del generador de IA
 * (`BRAND_COLORS.primaryDark`): antes era un índigo suelto que hacía que un
 * botón insertado a mano desentonara con el resto del email. Sin
 * `border-radius`: Outlook de escritorio lo ignora, así que el mismo botón se
 * vería redondeado en unos clientes y cuadrado en otros.
 */
const EDITOR_BUTTON_STYLE = `display:inline-block;padding:12px 24px;background-color:${BRAND_COLORS.primaryDark};color:#ffffff !important;text-decoration:none;font-weight:600;font-size:16px;`;

interface VisualEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  /**
   * Nombre del negocio que se muestra en la cabecera y el pie del email
   * generado. El remitente del email es el negocio, no la plataforma:
   * cada llamada a este componente debe pasar el nombre real (organización
   * del hostelero) salvo en el editor de campañas de plataforma (superadmin
   * a hosteleros), donde corresponde "ReservaManager" de forma explícita.
   */
  brandName?: string;
  /**
   * "restaurant" (campañas de un hostelero a sus clientes) o "admin"
   * (campañas de la plataforma a hosteleros). Se pasa tal cual a
   * `wrapEmailContent()` de `@/lib/email-layout` — el envoltorio que este
   * editor reconstruye tras cada edición debe ser el MISMO que usan las
   * plantillas predefinidas y el generador de IA, nunca uno propio.
   */
  variant?: EmailLayoutVariant;
  /**
   * URL real de la página de reservas (widget) del local actual, usada SOLO
   * para que la vista previa muestre a dónde lleva el botón de reserva en
   * vez del marcador en crudo `{{bookingUrl}}`. Nunca se guarda: `emit()`
   * revierte el `href` al marcador literal antes de llamar a `onChange`, así
   * que el HTML persistido en la campaña sigue llevando `{{bookingUrl}}`
   * (el envío real lo sustituye en el backend, igual que `{{unsubscribeUrl}}`).
   * Si no se pasa (p. ej. editor de campañas de plataforma, que no usa este
   * marcador), no se aplica ninguna sustitución.
   */
  previewBookingUrl?: string;
  /**
   * Si el flujo de envío de esta página sustituye el marcador `{{bookingUrl}}`
   * (campañas de un restaurante a sus comensales). Solo entonces el botón que
   * inserta la barra de herramientas apunta al marcador; en las campañas de
   * plataforma (superadmin → hosteleros) el envío NO lo sustituye, así que
   * allí el botón sigue saliendo con `href="#"` como hasta ahora — de lo
   * contrario al destinatario le llegaría el texto literal `{{bookingUrl}}`
   * dentro del enlace.
   *
   * Es una bandera EXPLÍCITA a propósito, en vez de deducirla de
   * `previewBookingUrl`: esa llega vacía también en el caso restaurante
   * mientras se cargan los locales, y el href del botón no puede depender de
   * un dato que aparece tarde.
   */
  supportsBookingUrl?: boolean;
}

const COLORS = [
  "#000000",
  "#1a1a1a",
  "#4a4a4a",
  "#7c3aed",
  "#2563eb",
  "#059669",
  "#d97706",
  "#dc2626",
  "#db2777",
  "#6366f1",
];

const FONT_SIZES = [
  { label: "Pequeño", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Grande", value: "4" },
  { label: "Muy grande", value: "5" },
];

export default function VisualEmailEditor({
  value,
  onChange,
  brandName = "ReservaManager",
  variant = "restaurant",
  previewBookingUrl,
  supportsBookingUrl = false,
}: VisualEmailEditorProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showColors, setShowColors] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageTab, setImageTab] = useState<"upload" | "url">("upload");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const [isReady, setIsReady] = useState(false);

  // Ref con el `value` más reciente, para leerlo desde callbacks que no se
  // recrean en cada render (ver `initShell` más abajo). Se actualiza en el
  // cuerpo del render, no en un efecto, para que esté al día antes de que
  // corra cualquier timeout/callback asíncrono.
  const valueRef = useRef(value);
  valueRef.current = value;

  // Ref con el `previewBookingUrl` más reciente, por el mismo motivo que
  // `valueRef`: se lee desde `initShell` (creado una sola vez) y desde el
  // efecto de resincronización, evitando cerrar sobre un valor obsoleto.
  const previewBookingUrlRef = useRef(previewBookingUrl);
  previewBookingUrlRef.current = previewBookingUrl;

  // Último HTML que este editor ya refleja en su <body> editable (ya sea
  // porque lo generamos nosotros con `onChange`, ya sea porque lo aplicamos
  // desde un `value` externo). Evita el bucle de re-envoltura: sin esto,
  // cada eco de nuestro propio `onChange` disparaba una reinicialización
  // completa del iframe, que a su vez volvía a envolver un contenido que
  // YA estaba envuelto, apilando cabeceras y pies en cada edición.
  const lastSyncedRef = useRef<string | null>(null);

  // Si el `value` cargado NO llevaba nuestros marcadores (HTML generado por
  // la IA, una plantilla predefinida del selector, o pegado a mano en la
  // pestaña HTML), no sabemos qué forma tiene su propia cabecera/pie: en ese
  // caso `emit()` NO añade una segunda banda de marca encima (evita
  // duplicar cabecera/pie), solo reconstruye un documento válido alrededor
  // del contenido tal cual. `externalHeadStylesRef` conserva cualquier
  // `<style>` que ese documento externo llevara en su `<head>` (formato
  // heredado del editor visual anterior a este), para no perder el estilo
  // de clases CSS que ya no emitimos.
  const useTemplateWrapRef = useRef(true);
  const externalHeadStylesRef = useRef("");

  const getDoc = useCallback(() => {
    return (
      iframeRef.current?.contentDocument ||
      iframeRef.current?.contentWindow?.document
    );
  }, []);

  // Crea el documento del iframe UNA sola vez (no depende de `value`, así
  // que su identidad nunca cambia y el efecto que lo engancha solo corre en
  // el montaje). El contenido inicial se aplica aparte, leyendo siempre el
  // `value` más reciente vía `valueRef`.
  const initShell = useCallback(() => {
    const doc = getDoc();
    if (!doc) return;

    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
<head>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #1a1a1a;
      padding: 20px;
      margin: 0;
      min-height: 100%;
      outline: none;
    }
    body:empty:before {
      content: 'Escribe aquí el contenido de tu email...';
      color: #9ca3af;
      font-style: italic;
    }
    a { color: #7c3aed; }
    img { max-width: 100%; height: auto; }
    h1 { font-size: 24px; margin: 16px 0 8px; }
    h2 { font-size: 20px; margin: 14px 0 6px; }
    p { margin: 8px 0; }
    hr { border: none; border-top: 1px solid #e5e7eb; margin: 16px 0; }
    blockquote { border-left: 3px solid #7c3aed; padding-left: 12px; margin: 8px 0; color: #6b7280; }
  </style>
</head>
<body contenteditable="true"></body>
</html>`);
    doc.close();

    // Wait for iframe to be ready then set content
    setTimeout(() => {
      const body = doc.body;
      if (body) {
        const initialValue = valueRef.current;
        const extracted = extractBodyContent(initialValue);
        body.innerHTML = applyBookingUrlPreview(
          extracted.content,
          previewBookingUrlRef.current,
        );
        useTemplateWrapRef.current = extracted.useTemplateWrap;
        externalHeadStylesRef.current = extracted.headStyles;
        lastSyncedRef.current = initialValue;
        setIsReady(true);
      }
    }, 100);
  }, [getDoc]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframe.addEventListener("load", initShell);
    // Also try to init immediately for already loaded iframes
    initShell();

    return () => {
      iframe.removeEventListener("load", initShell);
    };
  }, [initShell]);

  // Re-sincroniza el contenido del iframe cuando `value` cambia por una
  // razón EXTERNA (cambiar de campaña, generar con IA, pegar HTML a mano y
  // volver a la pestaña visual...). Si el cambio es simplemente el eco de
  // nuestro propio `onChange`, se ignora: el `<body>` editable ya refleja
  // ese contenido y reescribirlo de nuevo perdería el foco/cursor del
  // usuario y volvería a envolver un HTML que ya está envuelto.
  useEffect(() => {
    if (!isReady) return;
    if (value === lastSyncedRef.current) return;
    const doc = getDoc();
    if (!doc?.body) return;
    const extracted = extractBodyContent(value);
    doc.body.innerHTML = applyBookingUrlPreview(
      extracted.content,
      previewBookingUrlRef.current,
    );
    useTemplateWrapRef.current = extracted.useTemplateWrap;
    externalHeadStylesRef.current = extracted.headStyles;
    lastSyncedRef.current = value;
  }, [value, isReady, getDoc]);

  // Sync changes from iframe to parent
  useEffect(() => {
    if (!isReady) return;
    const doc = getDoc();
    if (!doc?.body) return;

    const emit = () => {
      // Se serializa desde un CLON, nunca desde `doc.body` directamente: así
      // revertir el `href` de vista previa a `{{bookingUrl}}` (ver
      // `revertBookingUrlPreview`) no toca el DOM editable real, y el enlace
      // sigue mostrando la URL real mientras el usuario edita.
      const clone = doc.body.cloneNode(true) as HTMLElement;
      revertBookingUrlPreview(clone);
      const bodyHtml = clone.innerHTML;
      // Contenido "marcado" (propio o heredado ya normalizado): se envuelve
      // con la plantilla nueva de marca. Contenido externo sin reconocer
      // (IA, plantillas del selector, HTML pegado a mano): se reconstruye
      // un documento válido SIN añadir una segunda cabecera/pie, para no
      // duplicar la banda de marca que ese documento ya trae consigo.
      // Mismo envoltorio único que usan las plantillas predefinidas y la IA
      // (`wrapEmailContent()` de `@/lib/email-layout`): este editor ya no
      // construye su propio documento. Los marcadores de contenido van
      // DENTRO del fragmento que recibe `wrapEmailContent`, así que siguen
      // sirviendo para que `extractBodyContent` recupere solo el contenido
      // editable la próxima vez (sin arrastrar cabecera/pie).
      const fullHtml = useTemplateWrapRef.current
        ? wrapEmailContent({
            variant,
            brandName,
            content: `${CONTENT_MARKER_START}${bodyHtml}${CONTENT_MARKER_END}`,
          })
        : wrapExternalContent(bodyHtml, externalHeadStylesRef.current);
      lastSyncedRef.current = fullHtml;
      onChange(fullHtml);
    };

    const observer = new MutationObserver(emit);

    doc.body.addEventListener("input", emit);
    observer.observe(doc.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    return () => {
      observer.disconnect();
      doc.body?.removeEventListener("input", emit);
    };
  }, [isReady, getDoc, onChange, brandName, variant]);

  const execCommand = (command: string, value?: string) => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    doc.execCommand(command, false, value);
  };

  const insertLink = () => {
    if (!linkUrl) return;
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    if (linkText) {
      doc.execCommand(
        "insertHTML",
        false,
        `<a href="${linkUrl}" target="_blank">${linkText}</a>`,
      );
    } else {
      doc.execCommand("createLink", false, linkUrl);
    }
    setShowLinkModal(false);
    setLinkUrl("");
    setLinkText("");
  };

  /**
   * Inserta una imagen con estilos INLINE (no depende de la hoja `<style>`
   * del editor, que no viaja en el HTML final del email) y con `alt`, para
   * que se comporte bien en clientes de correo que ignoran CSS externo o
   * bloquean imágenes por defecto.
   */
  const insertImageHtml = (url: string, alt: string) => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    // `width` (además de `max-width` en el `style`) para que Outlook de
    // escritorio (ignora `max-width`) no la renderice a su tamaño intrínseco
    // y desborde la tabla de 600px del email.
    doc.execCommand(
      "insertHTML",
      false,
      `<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" width="552" style="display:block;width:100%;max-width:552px;height:auto;border:0;margin:0 0 16px;" />`,
    );
  };

  /** Último segmento de una URL, sin query string ni extensión: mejor que
   * dejar el `alt` vacío cuando el usuario inserta una imagen por URL. */
  const deriveAltFromUrl = (url: string): string => {
    try {
      const path = new URL(url, window.location.href).pathname;
      const last = path.split("/").filter(Boolean).pop() || "";
      return last.replace(/\.[^./]+$/, "");
    } catch {
      return "";
    }
  };

  const insertImage = () => {
    if (!imageUrl) return;
    insertImageHtml(imageUrl, deriveAltFromUrl(imageUrl));
    setShowImageModal(false);
    setImageUrl("");
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setImageUploadError("Solo se permiten archivos de imagen");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageUploadError("La imagen no puede superar 5 MB");
      return;
    }

    setImageUploading(true);
    setImageUploadError("");

    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target?.result as string;
        try {
          // El backend sube la imagen a Storage y devuelve su URL pública
          // (nunca el propio data URI: incrustarlo aquí es justo el bug de
          // los emails recortados por Gmail que esta tarea corrige).
          const response = await api.post("/email-campaigns/upload-image", {
            file: base64,
            filename: file.name,
          });
          const url: string = response.data.data.url;
          const altText = file.name.replace(/\.[^./]+$/, "");
          insertImageHtml(url, altText);
          setShowImageModal(false);
          setImageUrl("");
        } catch (err) {
          setImageUploadError(
            getUploadErrorMessage(
              err,
              "Error al subir la imagen. Inténtalo de nuevo.",
            ),
          );
        } finally {
          setImageUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setImageUploadError("Error al leer el archivo.");
      setImageUploading(false);
    }

    // Reset file input so same file can be re-selected if needed
    e.target.value = "";
  };

  const insertButton = () => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    // El botón apunta a la página de reservas SOLO donde el envío sustituye el
    // marcador (ver `supportsBookingUrl`). En el resto sigue siendo `#`.
    const href = supportsBookingUrl ? BOOKING_URL_PLACEHOLDER : "#";
    // Estilo inline: el HTML final del email no lleva `<style>`, así que la
    // clase `.btn-email` que usaba el editor no sobreviviría al envío.
    const buttonHtml = `<p style="margin:8px 0;"><a href="${href}" style="${EDITOR_BUTTON_STYLE}">Botón de acción</a></p>`;
    // Se inserta con la vista previa YA aplicada para que el botón recién
    // creado enseñe la URL real igual que los que vienen de una plantilla (que
    // pasan por `applyBookingUrlPreview` al resincronizar el iframe). `emit()`
    // lo revierte al marcador antes de guardar, así que el HTML persistido es
    // idéntico en ambos casos.
    doc.execCommand(
      "insertHTML",
      false,
      applyBookingUrlPreview(buttonHtml, previewBookingUrlRef.current),
    );
  };

  const insertDivider = () => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    doc.execCommand("insertHTML", false, "<hr/>");
  };

  const ToolbarButton = ({
    onClick,
    active,
    title,
    children,
  }: {
    onClick: () => void;
    active?: boolean;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
        active ? "bg-gray-200 text-primary-600" : "text-gray-600"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
        {/* Undo/Redo */}
        <ToolbarButton onClick={() => execCommand("undo")} title="Deshacer">
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand("redo")} title="Rehacer">
          <Redo className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Text Format */}
        <ToolbarButton onClick={() => execCommand("bold")} title="Negrita">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand("italic")} title="Cursiva">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => execCommand("underline")}
          title="Subrayado"
        >
          <Underline className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Headings */}
        <ToolbarButton
          onClick={() => execCommand("formatBlock", "h1")}
          title="Título 1"
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => execCommand("formatBlock", "h2")}
          title="Título 2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => execCommand("formatBlock", "p")}
          title="Párrafo"
        >
          <Type className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Font Size */}
        <div className="relative">
          <ToolbarButton
            onClick={() => {
              setShowFontSize(!showFontSize);
              setShowColors(false);
            }}
            title="Tamaño"
          >
            <span className="text-xs font-bold w-4 h-4 flex items-center justify-center">
              A
            </span>
          </ToolbarButton>
          {showFontSize && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
              {FONT_SIZES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => {
                    execCommand("fontSize", s.value);
                    setShowFontSize(false);
                  }}
                  className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100"
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Color */}
        <div className="relative">
          <ToolbarButton
            onClick={() => {
              setShowColors(!showColors);
              setShowFontSize(false);
            }}
            title="Color de texto"
          >
            <Palette className="w-4 h-4" />
          </ToolbarButton>
          {showColors && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-2">
              <div className="grid grid-cols-5 gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      execCommand("foreColor", c);
                      setShowColors(false);
                    }}
                    className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Alignment */}
        <ToolbarButton
          onClick={() => execCommand("justifyLeft")}
          title="Alinear izquierda"
        >
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => execCommand("justifyCenter")}
          title="Centrar"
        >
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => execCommand("justifyRight")}
          title="Alinear derecha"
        >
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Lists */}
        <ToolbarButton
          onClick={() => execCommand("insertUnorderedList")}
          title="Lista"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => execCommand("insertOrderedList")}
          title="Lista numerada"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Insert */}
        <ToolbarButton
          onClick={() => {
            setShowLinkModal(true);
            setShowColors(false);
            setShowFontSize(false);
          }}
          title="Insertar enlace"
        >
          <Link className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            setShowImageModal(true);
            setShowColors(false);
            setShowFontSize(false);
          }}
          title="Insertar imagen"
        >
          <Image className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={insertDivider} title="Separador">
          <Minus className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={insertButton} title="Insertar botón">
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Blockquote */}
        <ToolbarButton
          onClick={() => execCommand("formatBlock", "blockquote")}
          title="Cita"
        >
          <span className="text-sm font-serif w-4 h-4 flex items-center justify-center">
            "
          </span>
        </ToolbarButton>
      </div>

      {/* Editor iframe */}
      <iframe
        ref={iframeRef}
        className="w-full border-0"
        style={{ height: "400px" }}
        title="Editor visual"
      />

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Insertar enlace
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Texto (opcional)
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Texto del enlace"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl("");
                  setLinkText("");
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={insertLink}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Insertar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Insertar imagen
            </h3>

            {/* Tabs */}
            <div className="flex border border-gray-200 rounded-lg mb-4 overflow-hidden">
              <button
                type="button"
                onClick={() => { setImageTab("upload"); setImageUploadError(""); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                  imageTab === "upload"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Upload className="w-4 h-4" />
                Subir archivo
              </button>
              <button
                type="button"
                onClick={() => { setImageTab("url"); setImageUploadError(""); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                  imageTab === "url"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Image className="w-4 h-4" />
                Desde URL
              </button>
            </div>

            {imageTab === "upload" ? (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={imageUploading}
                  className="w-full flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-300 rounded-lg p-8 text-gray-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {imageUploading ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                      <span className="text-sm font-medium text-primary-600">Subiendo imagen...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8" />
                      <div className="text-center">
                        <p className="text-sm font-medium">Haz clic para seleccionar una imagen</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP · Máx. 5 MB</p>
                      </div>
                    </>
                  )}
                </button>
                {imageUploadError && (
                  <p className="text-xs text-red-600 mt-2">{imageUploadError}</p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de la imagen
                </label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-1">
                  Pega la URL de una imagen ya subida
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowImageModal(false);
                  setImageUrl("");
                  setImageUploadError("");
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              {imageTab === "url" && (
                <button
                  type="button"
                  onClick={insertImage}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Insertar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

// Marcadores que delimitan el contenido editable dentro del HTML final. Con
// ellos, `extractBodyContent` recupera SIEMPRE el contenido real (nunca la
// cabecera/pie de marca que añade `wrapEmailContent()`), así que envolver
// dos veces seguidas nunca anida cabeceras ni infla el HTML: cada ciclo
// edición → onChange → value vuelve a partir del mismo contenido "plano".
const CONTENT_MARKER_START = "<!--rm-content-start-->";
const CONTENT_MARKER_END = "<!--rm-content-end-->";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Marcador de reserva (mismo patrón que `{{unsubscribeUrl}}`): el backend lo
// sustituye por la URL real del widget del local al enviar la campaña (ver
// `runCampaignSending`). Aquí solo se usa para que la vista previa del editor
// muestre la URL real en vez del texto en crudo — el HTML guardado conserva
// siempre el marcador literal, ver `revertBookingUrlPreview`.
const BOOKING_URL_PLACEHOLDER = "{{bookingUrl}}";
const BOOKING_URL_HREF_MARKER = `href="${BOOKING_URL_PLACEHOLDER}"`;
const BOOKING_URL_PREVIEW_ATTR = "data-rm-booking-preview";

/**
 * Sustituye, solo para MOSTRAR en el `<body>` editable, `href="{{bookingUrl}}"`
 * por la URL real de reservas del local actual, marcando el enlace con
 * `data-rm-booking-preview` para poder revertirlo sin ambigüedad antes de
 * emitir cambios (ver `revertBookingUrlPreview`). Si no hay URL de vista
 * previa disponible (`previewUrl` vacío — p. ej. el editor de campañas de
 * plataforma, que no pasa esta prop), el contenido no se toca y el marcador
 * se queda tal cual, igual que el resto de variables (`{{customerName}}`,
 * `{{unsubscribeUrl}}`).
 */
function applyBookingUrlPreview(html: string, previewUrl?: string): string {
  if (!previewUrl) return html;
  // `split/join` en vez de `replaceAll` (target ES2020 del proyecto, sin
  // `String.prototype.replaceAll`): sustitución literal, sin regex.
  return html
    .split(BOOKING_URL_HREF_MARKER)
    .join(`href="${escapeHtml(previewUrl)}" ${BOOKING_URL_PREVIEW_ATTR}="1"`);
}

/**
 * Revierte, IN-PLACE sobre `root` (siempre un clon desatendido de `doc.body`,
 * nunca el DOM editable real — ver `emit()`), cualquier enlace marcado por
 * `applyBookingUrlPreview` de vuelta al marcador literal `{{bookingUrl}}`.
 * Así el HTML que se guarda en la campaña nunca lleva la URL de vista previa
 * incrustada (rompería el envío a otros locales / la propia sustitución del
 * backend), mientras el enlace que el usuario ve mientras edita sigue
 * mostrando la URL real.
 */
function revertBookingUrlPreview(root: Element): void {
  root.querySelectorAll(`[${BOOKING_URL_PREVIEW_ATTR}]`).forEach((el) => {
    el.setAttribute("href", BOOKING_URL_PLACEHOLDER);
    el.removeAttribute(BOOKING_URL_PREVIEW_ATTR);
  });
}

/**
 * Mensaje de error que devuelve el backend (en español), en vez del
 * genérico de axios. Sin `any`: se valida la forma del error a mano.
 */
function getUploadErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { message?: string } } })
      .response;
    return response?.data?.message || fallback;
  }
  return fallback;
}

interface ExtractedContent {
  /** Contenido a cargar en el `<body>` editable del iframe. */
  content: string;
  /**
   * true si al emitir cambios hay que envolver con `wrapEmailContent()`
   * (contenido "propio": ya llevaba nuestros marcadores, o es el interior
   * ya normalizado del formato heredado `.content`). false si el HTML de
   * origen es externo y no reconocido (IA, plantillas del selector, HTML
   * pegado a mano): en ese caso no se añade una segunda cabecera/pie.
   */
  useTemplateWrap: boolean;
  /** `<style>` del `<head>` original, solo relevante si !useTemplateWrap. */
  headStyles: string;
}

function extractHeadStyles(html: string): string {
  const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
  if (!headMatch) return "";
  const styleMatches = headMatch[1].match(/<style[\s\S]*?<\/style>/gi);
  return styleMatches ? styleMatches.join("\n") : "";
}

/**
 * Reconoce el formato heredado del editor visual anterior a este (marcado
 * `.container > .card > .logo/.content/.footer`, con `<style>` propio en el
 * `<head>`). Si lo encuentra, devuelve solo el interior de `.content` (el
 * contenido real editado por el usuario), descartando `.logo`/`.footer`
 * (cabecera/pie de marca antiguos que la plantilla nueva ya sustituye) y
 * normalizando a estilos inline lo que dependía del `<style>` descartado
 * (botones `.btn-email`, imágenes sin `style`).
 */
function extractLegacyContent(html: string): string | null {
  if (typeof DOMParser === "undefined") return null;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const el = doc.querySelector(".content");
    if (!el) return null;

    el.querySelectorAll("a.btn-email").forEach((a) => {
      a.removeAttribute("class");
      a.setAttribute("style", EDITOR_BUTTON_STYLE);
    });
    el.querySelectorAll("img:not([style])").forEach((img) => {
      img.setAttribute(
        "style",
        "display:block;max-width:100%;height:auto;border:0;",
      );
    });

    return el.innerHTML;
  } catch {
    return null;
  }
}

function extractBodyContent(html: string): ExtractedContent {
  if (!html) return { content: "", useTemplateWrap: true, headStyles: "" };

  const markerStart = html.indexOf(CONTENT_MARKER_START);
  if (markerStart !== -1) {
    const start = markerStart + CONTENT_MARKER_START.length;
    const end = html.indexOf(CONTENT_MARKER_END, start);
    if (end !== -1) {
      return {
        content: html.slice(start, end),
        useTemplateWrap: true,
        headStyles: "",
      };
    }
  }

  const legacyContent = extractLegacyContent(html);
  if (legacyContent !== null) {
    return { content: legacyContent, useTemplateWrap: true, headStyles: "" };
  }

  // HTML externo sin marcadores propios ni formato heredado reconocido:
  // viene de una plantilla externa (IA, las plantillas predefinidas del
  // selector) o de la pestaña HTML. No sabemos qué forma tiene su propia
  // cabecera/pie, así que se toma tal cual el contenido del `<body>` y NO
  // se envuelve de nuevo con la plantilla de marca (evita duplicar
  // cabecera/pie); se conserva cualquier `<style>` de su `<head>`.
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    return {
      content: bodyMatch[1],
      useTemplateWrap: false,
      headStyles: extractHeadStyles(html),
    };
  }
  // Documento incompleto (con <html>/<!DOCTYPE> pero sin <body> reconocible)
  if (html.includes("<html") || html.includes("<!DOCTYPE")) {
    return {
      content: "",
      useTemplateWrap: false,
      headStyles: extractHeadStyles(html),
    };
  }
  // Fragmento de contenido plano (sin doctype/html/body): se trata como
  // contenido propio, para que la plantilla de marca lo envuelva como antes.
  return { content: html, useTemplateWrap: true, headStyles: "" };
}

/**
 * Reconstruye un documento HTML válido alrededor de contenido EXTERNO (IA,
 * plantillas predefinidas del selector, HTML pegado a mano) sin añadir
 * ninguna banda de marca ni pie nuevos: ese contenido ya trae su propia
 * cabecera/pie, y volver a envolverlo duplicaría ambos. Se conserva
 * cualquier `<style>` que el documento original llevara.
 *
 * El fondo del `<body>` (`#f4f4f5`) coincide a propósito con el de
 * `wrapEmailContent()` (`@/lib/email-layout`): en clientes que sí respetan
 * los estilos del `<body>` (Apple Mail, Mail de iOS) la franja bajo la
 * tarjeta de 600px debe verse igual la use o no el editor visual.
 */
function wrapExternalContent(bodyContent: string, headStyles: string): string {
  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
${headStyles}
</head>
<body style="margin:0;padding:0;background-color:#f4f4f5;">
${bodyContent}
</body>
</html>`;
}
